// Tailor fill-campaign client (FXC-9009 follow-on) — the "fill the database, don't grid it" tier
// of the High-Lift multifidelity workbench.
//
// A campaign is a SERVER-SIDE adaptive loop: the user boxes the operating/geometry axes with
// RANGES, and thread-api's campaign runner repeatedly asks the residual-GP acquisition where it
// learns the most inside that box, dispatching each batch through the EXISTING /highlift/solve
// path (cache → local V100 w/ NN warm-start → cloud Flow360 fork). This module is the thin wire
// client: create / list / fetch / control. The workbench polls `fetchHighliftCampaign` while a
// campaign runs and refreshes the ADB grid as completed counts tick up — the same post-solve
// seam runSolve uses.
//
// Server: thread/thread-api/app/routes/highlift_campaigns.py
//   POST   /projects/{id}/highlift/campaigns?branch=
//   GET    /projects/{id}/highlift/campaigns?branch=
//   GET    /projects/{id}/highlift/campaigns/{campaign_id}?branch=
//   POST   /projects/{id}/highlift/campaigns/{campaign_id}/control?branch=
//
// Campaign state persists as ONE V3 `thread_core.operations` row (metadata_json carries the
// {config, state} blobs mirrored by the types below) — no v1/v2 schema growth.

import { apiClient } from "./client";
import type { WarmStartSpec } from "./highlift";

// --- axis spec ----------------------------------------------------------------

/** One axis of the campaign box, in CANONICAL composite_db axis names (alpha_deg, mach,
 *  re_chord_millions, disk_thrust_loading, flap_deploy_deg, flaperon_deflection_deg — see
 *  campaignMath.FILL_AXES for the panel↔canonical mapping). `range` axes span [min, max];
 *  `hold` axes pin the acquisition to `value` (a degenerate bound server-side). Note Re is
 *  boxed on the canonical MILLIONS axis — the runner converts back to raw `reynolds` per
 *  solve point, exactly as SweepPanel does on submit. */
export interface CampaignAxisSpec {
  axis: string;
  mode: "range" | "hold";
  min?: number;
  max?: number;
  value?: number;
}

// --- create request -----------------------------------------------------------

/** The solve-request template every campaign batch is built from — the SAME records the
 *  workbench's runSolve sends on /highlift/solve (design/operation/elements/warm_start/
 *  geometry_slug), captured once at campaign create so every batch solves the geometry the
 *  user was looking at. Per-point fields (points, local_inputs, progress_token) are the
 *  runner's job — it re-derives them per recommendation, patching the geometry axes through
 *  the server-side geometry CLI when deploy/flaperon vary. */
export interface CampaignSolveTemplate {
  design?: Record<string, unknown>;
  operation?: Record<string, unknown>;
  elements?: { name: string; contour: [number, number][] }[];
  max_steps?: number;
  warm_start?: WarmStartSpec | null;
  geometry_slug?: string | null;
  forbid_cloud_fallback?: boolean;
}

export interface CampaignCreateRequest {
  /** Human label for the campaign list; server defaults one when omitted. */
  name?: string;
  /** Composite output the acquisition targets. Default "cl". */
  quantity?: string;
  /** Total solve-point budget across all iterations (4..64). */
  budget: number;
  /** Points per acquisition batch (1..8 — the /highlift/solve single-call cap). */
  batch_size?: number;
  /** Optional early-stop: finish once box coverage reaches this fraction (0..1). */
  target_coverage?: number | null;
  axes: CampaignAxisSpec[];
  solve_template: CampaignSolveTemplate;
  /** Full HighLiftAirfoil config so the runner can re-mesh geometry-axis points through the
   *  server-side geometry CLI. Omit only when every geometry axis is held (the server 400s a
   *  geometry-axis range without it). Typed loosely to keep this wire module free of feature
   *  imports (same posture as SolveRequest.design). */
  highlift_config?: unknown;
}

// --- state (mirrors the backend's persisted metadata_json) ---------------------

export type CampaignStatus = "running" | "paused" | "completed" | "failed" | "stopped";

/** One dispatched point inside an iteration. `backend` is which tier serviced it
 *  ("cache" | "local" | "cloud"); cache hits and inline-CL local solves are `done`
 *  immediately, cloud forks stay `pending` until the case poller ingests them. */
export interface CampaignIterationPoint {
  condition: Record<string, number | string>;
  run_path: string | null;
  backend: string;
  status: "pending" | "done" | "failed";
  CL?: number | null;
  CD?: number | null;
  /** Per-point acquisition rationale VERBATIM (the fill replay narrates it).
   *  Campaigns persisted before this field exist without it — fall back to the
   *  iteration-level `reasons_top`. */
  reasons?: string[];
  score?: number | null;
  purpose?: string | null;
  /** Rich per-signal acquisition breakdown (FXC-9009 "why" narration). NEW
   *  campaigns persist these; older ones (the beecbb20 demo) omit them, so
   *  every consumer must treat them as optional and degrade to `reasons`. */
  components?: Record<string, number> | null;
  classification?: string | null;
  disagreement_z?: number | null;
}

/** One acquisition→dispatch→settle cycle of the runner loop. `reasons_top` are the
 *  acquisition's human-readable rationale strings VERBATIM — the panel feed shows them
 *  unedited, so the user sees exactly why Tailor spent each batch where it did. */
export interface CampaignIteration {
  index: number;
  requested_at: string;
  reasons_top: string[];
  engine: string;
  points: CampaignIterationPoint[];
  coverage_after: number;
  surrogate?: Record<string, unknown>;
}

export interface CampaignState {
  status: CampaignStatus;
  note: string | null;
  dispatched: number;
  completed: number;
  failed: number;
  cache_hits: number;
  warm_started: number;
  /** Latest box coverage 0..1 (256-probe Halton cloud vs solved HF rows). */
  coverage: number;
  iterations: CampaignIteration[];
}

/** The persisted campaign config (the create request + server-stamped provenance). */
export interface CampaignConfig {
  name: string;
  quantity: string;
  budget: number;
  batch_size: number;
  target_coverage: number | null;
  axes: CampaignAxisSpec[];
  solve_template: CampaignSolveTemplate;
  highlift_config?: unknown;
  branch?: string;
  owner_id?: string;
  project_id?: string;
  created_at?: string;
}

/** Full campaign detail (GET /{campaign_id}). `runner_alive` is the honest liveness flag:
 *  an API restart orphans in-flight runners (DB status stays 'running'), so the panel offers
 *  Resume — POST control {action:"resume"} restarts the loop — instead of lying about
 *  progress that will never come. */
export interface CampaignDetail {
  campaign_id: string;
  config: CampaignConfig;
  state: CampaignState;
  runner_alive?: boolean;
}

export interface CampaignCreateResponse {
  campaign_id: string;
  state: CampaignState;
}

/** One row of the campaign list (GET "") — enough for the panel to re-attach to a
 *  running campaign after a page refresh without fetching every detail blob. */
export interface CampaignSummary {
  campaign_id: string;
  name: string;
  status: CampaignStatus;
  dispatched: number;
  completed: number;
  budget: number;
  coverage: number;
  created_at: string;
}

export type CampaignControlAction = "pause" | "resume" | "stop";

/** Control acks with the campaign's post-action status; the panel refetches the detail
 *  rather than trusting this snapshot (the runner persists asynchronously). */
export interface CampaignControlResponse {
  campaign_id: string;
  status: CampaignStatus;
}

// --- wire calls ----------------------------------------------------------------

/** Start a fill campaign: validates the box (geometry-axis ranges need a local-solve host),
 *  persists the thread_core.operations row, and starts the server-side runner task. */
export async function createHighliftCampaign(
  projectId: string,
  branch: string,
  body: CampaignCreateRequest,
): Promise<CampaignCreateResponse> {
  const params = new URLSearchParams({ branch });
  return apiClient<CampaignCreateResponse>(
    `/projects/${projectId}/highlift/campaigns?${params.toString()}`,
    { method: "POST", body },
  );
}

/** List the project's campaigns, newest first (server caps at 20). Used on mount to
 *  re-attach the panel to a still-running campaign across page refreshes. */
export async function listHighliftCampaigns(
  projectId: string,
  branch = "main",
): Promise<CampaignSummary[]> {
  const params = new URLSearchParams({ branch });
  const res = await apiClient<CampaignSummary[] | { campaigns: CampaignSummary[] }>(
    `/projects/${projectId}/highlift/campaigns?${params.toString()}`,
  );
  // The contract is a bare array; tolerate a {campaigns: []} envelope so a wrapper-style
  // server response degrades to working instead of a runtime type error mid-demo.
  return Array.isArray(res) ? res : (res.campaigns ?? []);
}

/** Fetch one campaign's full state (the panel polls this every 4s while running). */
export async function fetchHighliftCampaign(
  projectId: string,
  campaignId: string,
  branch = "main",
): Promise<CampaignDetail> {
  const params = new URLSearchParams({ branch });
  return apiClient<CampaignDetail>(
    `/projects/${projectId}/highlift/campaigns/${campaignId}?${params.toString()}`,
  );
}

/** Pause / resume / stop a campaign. Resume also restarts an orphaned runner (one whose
 *  API host restarted mid-campaign — see CampaignDetail.runner_alive). */
export async function controlHighliftCampaign(
  projectId: string,
  campaignId: string,
  action: CampaignControlAction,
  branch = "main",
): Promise<CampaignControlResponse> {
  const params = new URLSearchParams({ branch });
  return apiClient<CampaignControlResponse>(
    `/projects/${projectId}/highlift/campaigns/${campaignId}/control?${params.toString()}`,
    { method: "POST", body: { action } },
  );
}
